import classNames from "classnames";
import Popover from "components/Popover";
import pluralize from "helpers/pluralize";
import useLabels from "hooks/useLabels";
import { CategoryAggregations } from "../types";

interface AggregationResultProps {
  aggregation: CategoryAggregations;
  value: number;
  winner?: boolean;
  tied?: boolean;
}

export const AggregationResult = ({
  aggregation,
  value,
  winner = false,
  tied = false,
}: AggregationResultProps) => {
  const [aggregationResultsLabels, aggregationResultsTooltips] = useLabels(
    "consensus-meter.aggregation-results.labels",
    "consensus-meter.aggregation-results.tooltips"
  );
  const formattedValue =
    aggregation === "citations"
      ? Intl.NumberFormat("en", {
          notation: "compact",
          compactDisplay: "short",
        })
          .format(value)
          .replace("K", "k") // default K replaced with k for thousands
      : Intl.NumberFormat("en-US", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
          useGrouping: false,
        }).format(value);

  const formattedLabels = (aggregation: CategoryAggregations) => {
    switch (aggregation) {
      case "tier_one_studies":
        return pluralize(aggregationResultsLabels[aggregation], {
          value: formattedValue,
        });
      case "journal_score":
        if (formattedValue === "0") {
          return "—";
        }
        return pluralize(aggregationResultsLabels[aggregation], {
          value: formattedValue,
        });
      default:
        return `${formattedValue} ${aggregationResultsLabels[aggregation]}`;
    }
  };

  const formattedTooltips = (aggregation: CategoryAggregations) => {
    switch (aggregation) {
      case "tier_one_studies":
      case "journal_score":
        return pluralize(aggregationResultsTooltips[aggregation], {
          value: formattedValue,
        });
      default:
        return `${formattedValue} ${aggregationResultsTooltips[aggregation]}`;
    }
  };

  return (
    <div className="flex items-start gap-1 justify-between">
      <div>
        <Popover
          tooltipContent={
            <p className="tiny-normal text-center max-w-[234px]">
              {formattedTooltips(aggregation)}
            </p>
          }
          placement="top"
          theme="dark"
          size="small"
          widthFull
        >
          <p
            className={classNames(
              "text-fg-base",
              winner ? "sm-md" : "sm-normal"
            )}
          >
            {formattedLabels(aggregation)}
          </p>
        </Popover>
      </div>
      {winner && (
        <i
          className={classNames(
            "text-chart-green-base bold-icon text-[16px]",
            tied ? "icon-equal" : "icon-crown"
          )}
        />
      )}
    </div>
  );
};
